import { API_URL as BASE_URL } from '../../lib/config';
const API_URL = `${BASE_URL}/platform-admin`;

export interface SystemEvent {
  id: string;
  organizationId: string | null;
  eventType: 'WEBHOOK_FAILURE' | 'INTEGRATION_SYNC_FAILURE' | 'SCHEDULED_JOB_FAILURE' | 'API_ERROR' | 'RATE_LIMIT_EXCEEDED' | 'DATABASE_ERROR' | 'EXTERNAL_SERVICE_ERROR' | 'AUTHENTICATION_FAILURE';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  source: string;
  operation: string;
  status: 'PENDING' | 'PROCESSING' | 'RESOLVED' | 'FAILED' | 'IGNORED';
  errorMessage: string | null;
  errorStack: string | null;
  metadata: any;
  userId: string | null;
  retryCount: number;
  maxRetries: number;
  nextRetryAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNotes: string | null;
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;
  organization?: {
    id: string;
    name: string;
  };
}

export interface EventStats {
  total: number;
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  criticalLast24h: number;
}

export interface GetEventsParams {
  eventType?: string;
  severity?: string;
  status?: string;
  organizationId?: string;
  startDate?: string;
  endDate?: string;
  skip?: number;
  take?: number;
}

export interface PaginatedEvents {
  data: SystemEvent[];
  total: number;
  skip: number;
  take: number;
}

export interface ResolveEventDto {
  notes: string;
}

export interface IgnoreEventDto {
  notes: string;
}

export const systemEventsService = {
  async getStats(token: string, organizationId?: string): Promise<EventStats> {
    const queryParams = new URLSearchParams();
    if (organizationId) queryParams.append('organizationId', organizationId);

    const url = `${API_URL}/events/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch event stats');
    }

    return response.json();
  },

  async getEvents(
    token: string,
    params?: GetEventsParams
  ): Promise<PaginatedEvents> {
    const queryParams = new URLSearchParams();
    if (params?.eventType) queryParams.append('eventType', params.eventType);
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.organizationId) queryParams.append('organizationId', params.organizationId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.take !== undefined) queryParams.append('take', params.take.toString());

    const url = `${API_URL}/events${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch events');
    }

    return response.json();
  },

  async getEventById(token: string, id: string): Promise<SystemEvent> {
    const response = await fetch(`${API_URL}/events/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch event');
    }

    return response.json();
  },

  async retryEvent(token: string, id: string): Promise<SystemEvent> {
    const response = await fetch(`${API_URL}/events/${id}/retry`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to retry event');
    }

    return response.json();
  },

  async resolveEvent(
    token: string,
    id: string,
    data: ResolveEventDto
  ): Promise<SystemEvent> {
    const response = await fetch(`${API_URL}/events/${id}/resolve`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to resolve event');
    }

    return response.json();
  },

  async ignoreEvent(
    token: string,
    id: string,
    data: IgnoreEventDto
  ): Promise<SystemEvent> {
    const response = await fetch(`${API_URL}/events/${id}/ignore`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to ignore event');
    }

    return response.json();
  },
};
